import { CurrencyInput } from 'cpool-components/input/CurrencyInput';
import { TextInput } from 'cpool-components/input/TextInput';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { LuFileText } from 'react-icons/lu';

import { BankCreditRequestFormSection } from '../BankCreditRequestFormSection';
import { useRefinanceAmounts } from '../RefinancePlanner/hooks/useRefinanceAmounts';
import type { SchemaCreditRequestForm } from '../schemas/creditRequestForm.schema';

type Props = {
    coApplicantHidden: boolean;
};

const LABEL_CLASS_NAME = 'h-8 w-44 shrink-0 justify-start';

export const LiabilitiesSummary: React.FC<Props> = ({ coApplicantHidden }) => {
    const { t } = useTranslation();
    const { availableCredit, kreditBetrag, totalFinancedAmount } = useRefinanceAmounts(coApplicantHidden);
    const [provision, verwendungszweck] = useWatch<SchemaCreditRequestForm, ['provision', 'verwendungszweck']>({
        name: ['provision', 'verwendungszweck'],
    });
    const commission = (kreditBetrag * (provision ?? 0)) / 100;

    return (
        <BankCreditRequestFormSection
            title={t('BANK_CREDIT_REQUEST.LIABILITIES_SUMMARY.TITLE')}
            icon={<LuFileText className="size-4" />}
            testId="liabilities-summary"
        >
            <div className="flex flex-col gap-2 rounded-md border border-grey-outline-light p-3">
                <CurrencyInput
                    id="liabilities-summary-liabilities"
                    label={t('BANK_CREDIT_REQUEST.LIABILITIES_SUMMARY.LIABILITIES_SUM')}
                    labelClassName={LABEL_CLASS_NAME}
                    value={totalFinancedAmount}
                    disabled
                />
                <div className="grid gap-2 lg:grid-cols-2">
                    <CurrencyInput
                        id="liabilities-summary-cash-share"
                        label={t('BANK_CREDIT_REQUEST.LIABILITIES_SUMMARY.CASH_SHARE')}
                        labelClassName={LABEL_CLASS_NAME}
                        value={availableCredit}
                        disabled
                    />
                    <TextInput
                        id="liabilities-summary-planned-for"
                        label={t('BANK_CREDIT_REQUEST.LIABILITIES_SUMMARY.PLANNED_FOR')}
                        labelClassName="h-8 w-24 shrink-0 justify-start"
                        value={verwendungszweck ? t(`FUNDING_TYPES.${verwendungszweck}`) : ''}
                        disabled
                    />
                </div>
                <CurrencyInput
                    id="liabilities-summary-sum"
                    label={t('BANK_CREDIT_REQUEST.LIABILITIES_SUMMARY.SUM')}
                    labelClassName={LABEL_CLASS_NAME}
                    value={kreditBetrag}
                    disabled
                />
                <CurrencyInput
                    id="liabilities-summary-commission"
                    label={t('BANK_CREDIT_REQUEST.LIABILITIES_SUMMARY.COMMISSION')}
                    labelClassName={LABEL_CLASS_NAME}
                    value={commission}
                    disabled
                />
                <CurrencyInput
                    id="liabilities-summary-total"
                    label={t('BANK_CREDIT_REQUEST.LIABILITIES_SUMMARY.TOTAL')}
                    labelClassName={`${LABEL_CLASS_NAME} font-bold`}
                    value={kreditBetrag + commission}
                    disabled
                />
            </div>
        </BankCreditRequestFormSection>
    );
};
